/**
 * Reusable Annual Compliance Dashboard.
 *
 * Renders % completion of yearly compliance milestones split by framework
 * (NIS2 vs EU AI Act). Works even when the tenant only uses ONE framework.
 *
 * Props:
 *  - compact:      boolean — hides milestone list & year navigator (for embedded card).
 *  - defaultYear:  number  — starting year (defaults to current year).
 *  - defaultFramework: 'all'|'nis2'|'ai_act' — starting framework filter.
 *  - hidePdfButton: boolean — hides PDF export.
 *  - title:        string — override card title.
 *  - onOpenFull:   () => void — optional link handler for compact "Ver detalhe" button.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  TrendingUp, ChevronLeft, ChevronRight, Loader2, ShieldCheck, BookOpen,
  Building2, CalendarClock, CheckCircle2, AlertTriangle, Clock, Download, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FRAMEWORK_META = {
  nis2:     { label: 'NIS2',       tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  ai_act:   { label: 'EU AI Act',  tone: 'bg-indigo-50 text-indigo-700 border-indigo-200',    icon: BookOpen },
  cross:    { label: 'Cross-reg',  tone: 'bg-slate-50 text-slate-700 border-slate-200',       icon: Building2 },
  internal: { label: 'Interno',    tone: 'bg-amber-50 text-amber-700 border-amber-200',       icon: CalendarClock },
};

const SEVERITY_META = {
  critical: { label: 'Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  high:     { label: 'Alto',    tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium:   { label: 'Médio',   tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:      { label: 'Baixo',   tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  info:     { label: 'Info',    tone: 'bg-slate-50 text-slate-700 border-slate-200' },
};

function formatDatePt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

const FRAMEWORK_FILTERS = [
  { value: 'all',    label: 'Todos' },
  { value: 'nis2',   label: 'NIS2' },
  { value: 'ai_act', label: 'EU AI Act' },
];

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Horizontal 12-month timeline with milestones as dots grouped by framework lane. */
function MilestoneTimeline({ year, milestones }) {
  const byFw = milestones.reduce((acc, m) => {
    const fw = m.framework || 'internal';
    (acc[fw] = acc[fw] || []).push(m);
    return acc;
  }, {});
  const orderedFws = ['nis2', 'ai_act', 'cross', 'internal'].filter(fw => byFw[fw]);
  const today = new Date();
  const isCurrentYear = today.getFullYear() === year;
  const todayPct = isCurrentYear
    ? ((today.getMonth() + today.getDate() / 31) / 12) * 100
    : null;

  const dotColor = (m) => {
    if (m.completed) return 'bg-emerald-500 border-emerald-600';
    if (m.overdue) return 'bg-red-500 border-red-600';
    if (m.severity === 'critical') return 'bg-red-400 border-red-500';
    if (m.severity === 'high') return 'bg-orange-400 border-orange-500';
    return 'bg-indigo-400 border-indigo-500';
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid="milestone-timeline">
      {/* Month header */}
      <div className="relative pl-24 mb-2">
        <div className="grid grid-cols-12 gap-0 border-b border-slate-200 pb-1 text-[10px] text-slate-500 font-semibold uppercase tracking-wide">
          {MONTHS_PT.map((m, i) => (
            <div key={i} className="text-center border-r border-slate-100 last:border-r-0 py-1">{m}</div>
          ))}
        </div>
      </div>
      {/* Lanes */}
      <div className="space-y-2">
        {orderedFws.map(fw => {
          const meta = FRAMEWORK_META[fw] || FRAMEWORK_META.internal;
          const Icon = meta.icon;
          return (
            <div key={fw} className="relative flex items-center" data-testid={`timeline-lane-${fw}`}>
              {/* Lane label */}
              <div className={`w-24 flex items-center gap-1.5 pr-2 flex-shrink-0`}>
                <Icon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{meta.label}</span>
              </div>
              {/* Track */}
              <div className="flex-1 relative h-10 bg-slate-50 rounded border border-slate-100 overflow-visible">
                {/* Month grid */}
                <div className="absolute inset-0 grid grid-cols-12 pointer-events-none">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="border-r border-slate-100 last:border-r-0" />
                  ))}
                </div>
                {/* Today marker */}
                {todayPct !== null && (
                  <div
                    className="absolute top-0 bottom-0 w-px bg-indigo-500 z-10"
                    style={{ left: `${todayPct}%` }}
                    title="Hoje"
                    data-testid="timeline-today-marker"
                  >
                    <div className="absolute -top-1 -translate-x-1/2 w-2 h-2 rounded-full bg-indigo-500" />
                  </div>
                )}
                {/* Milestone dots */}
                {byFw[fw].map(m => {
                  const d = new Date(m.date);
                  if (d.getFullYear() !== year) return null;
                  const pct = ((d.getMonth() + d.getDate() / 31) / 12) * 100;
                  return (
                    <div
                      key={m.id}
                      className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 ${dotColor(m)} shadow-sm hover:scale-150 hover:z-20 transition-transform cursor-help`}
                      style={{ left: `${pct}%` }}
                      title={`${m.date} · ${m.title} (${m.severity}${m.completed ? ' · concluído' : m.overdue ? ' · atraso' : ''})`}
                      data-testid={`timeline-dot-${m.id}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="mt-3 pl-24 flex items-center gap-3 flex-wrap text-[11px] text-slate-600">
        <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-emerald-600" /> Concluído</div>
        <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-red-600" /> Em atraso</div>
        <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 border-2 border-red-500" /> Crítico</div>
        <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 border-2 border-orange-500" /> Alto</div>
        <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400 border-2 border-indigo-500" /> Outros</div>
        {isCurrentYear && <div className="flex items-center gap-1"><span className="w-px h-3 bg-indigo-500" /> Hoje</div>}
      </div>
    </div>
  );
}

export function AnnualComplianceDashboard({
  compact = false,
  defaultYear = new Date().getFullYear(),
  defaultFramework = 'all',
  hidePdfButton = false,
  title = 'Painel de Cumprimento Anual',
  onOpenFull = null,
}) {
  const [year, setYear] = useState(defaultYear);
  const [fw, setFw] = useState(defaultFramework);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = { year };
        if (fw !== 'all') params.framework = fw;
        const res = await api.get('/compliance/calendar/annual-compliance', { params });
        if (!ignore) setData(res.data);
      } catch (err) {
        if (!ignore) toast.error('Falha ao carregar painel anual.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [year, fw]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({ year: String(year) });
      if (fw !== 'all') params.set('framework', fw);
      const res = await fetch(`${BACKEND_URL}/api/compliance/calendar/annual-compliance/pdf?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('http');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `painel-anual-${fw === 'all' ? 'todos' : fw}-${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Relatório PDF gerado.');
    } catch (err) {
      toast.error('Falha ao gerar PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card data-testid="annual-compliance-dashboard">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-600" /> {title}
            </CardTitle>
            {!compact && (
              <p className="text-xs text-slate-500 mt-1">
                Visão executiva do cumprimento de marcos por framework — funciona mesmo se apenas usar NIS2 ou apenas AI Act.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Framework filter */}
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="annual-fw-filter">
              {FRAMEWORK_FILTERS.map(f => (
                <button
                  key={f.value}
                  onClick={() => setFw(f.value)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    fw === f.value ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`annual-fw-${f.value}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {!compact && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} data-testid="annual-year-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-lg font-semibold text-slate-900 tabular-nums w-16 text-center" data-testid="annual-year-label">
                  {year}
                </div>
                <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} data-testid="annual-year-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            {!hidePdfButton && (
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="annual-pdf-btn">
                {downloading
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Download className="h-3.5 w-3.5 mr-1.5" />}
                PDF
              </Button>
            )}
            {compact && onOpenFull && (
              <Button variant="ghost" size="sm" onClick={onOpenFull} data-testid="annual-open-full-btn">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Detalhe
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={compact ? 'p-4 space-y-4' : 'p-6 space-y-6'}>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        ) : !data ? (
          <div className="text-center text-sm text-slate-400 py-8">Sem dados.</div>
        ) : (
          <>
            {/* Overall KPI */}
            <div className={`grid gap-3 ${compact ? 'grid-cols-4' : 'grid-cols-2 lg:grid-cols-4'}`}>
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Total {data.year}</div>
                <div className={`font-bold text-slate-900 tabular-nums ${compact ? 'text-xl' : 'text-3xl'}`} data-testid="annual-total">{data.overall.total}</div>
              </div>
              <div className="rounded-lg border border-emerald-200 p-3 bg-emerald-50">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Concluídos</div>
                <div className={`font-bold text-emerald-800 tabular-nums ${compact ? 'text-xl' : 'text-3xl'}`} data-testid="annual-completed">
                  {data.overall.completed}
                  <span className={`text-emerald-600 ml-1.5 ${compact ? 'text-xs' : 'text-sm'}`}>({data.overall.completed_pct}%)</span>
                </div>
              </div>
              <div className="rounded-lg border border-red-200 p-3 bg-red-50">
                <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-1">Em atraso</div>
                <div className={`font-bold text-red-800 tabular-nums ${compact ? 'text-xl' : 'text-3xl'}`} data-testid="annual-overdue">{data.overall.overdue}</div>
              </div>
              <div className="rounded-lg border border-orange-200 p-3 bg-orange-50">
                <div className="text-[10px] uppercase tracking-widest text-orange-700 font-semibold mb-1">Próximos</div>
                <div className={`font-bold text-orange-800 tabular-nums ${compact ? 'text-xl' : 'text-3xl'}`} data-testid="annual-upcoming">{data.overall.upcoming}</div>
              </div>
            </div>

            {/* Framework breakdown */}
            <div>
              {!compact && (
                <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">Cumprimento por framework</h3>
              )}
              {Object.keys(data.frameworks || {}).length === 0 ? (
                <div className="text-sm text-slate-400 italic py-4 text-center">
                  Sem marcos aplicáveis a {data.year}.
                </div>
              ) : (
                <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                  {Object.entries(data.frameworks).map(([fwKey, d]) => {
                    const meta = FRAMEWORK_META[fwKey] || FRAMEWORK_META.internal;
                    const Icon = meta.icon;
                    return (
                      <div key={fwKey} className={`border rounded-lg ${meta.tone} p-3`} data-testid={`annual-framework-${fwKey}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span className="font-semibold text-sm">{meta.label}</span>
                          </div>
                          <Badge variant="secondary" className="bg-white/60 text-[10px]">
                            {d.completed}/{d.total}
                          </Badge>
                        </div>
                        <div className="w-full bg-white/70 rounded-full h-2 mb-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2 rounded-full transition-all"
                            style={{ width: `${d.completed_pct}%` }}
                            data-testid={`annual-progress-${fwKey}`}
                          />
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="tabular-nums font-semibold">{d.completed_pct}%</span>
                          <div className="flex gap-2 text-slate-600">
                            <span>⚠ {d.overdue}</span>
                            <span>⏳ {d.upcoming}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Timeline visual — 12-month horizontal Gantt-style view (expanded mode only) */}
            {!compact && data.milestones.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                  Cronograma {data.year}
                </h3>
                <MilestoneTimeline year={data.year} milestones={data.milestones} />
              </div>
            )}

            {/* Milestone list (only in expanded mode) */}
            {!compact && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wide">
                  Todos os marcos de {data.year} ({data.milestones.length})
                </h3>
                {data.milestones.length === 0 ? (
                  <div className="text-sm text-slate-400 italic py-4 text-center">Sem marcos neste ano.</div>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                    {data.milestones.map(m => {
                      const meta = FRAMEWORK_META[m.framework] || FRAMEWORK_META.internal;
                      const sev = SEVERITY_META[m.severity] || SEVERITY_META.info;
                      return (
                        <div key={m.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`annual-milestone-${m.id}`}>
                          <div className="text-xs text-slate-500 tabular-nums w-24">{formatDatePt(m.date)}</div>
                          <Badge variant="secondary" className={meta.tone}>{meta.label}</Badge>
                          <Badge variant="secondary" className={sev.tone}>{sev.label}</Badge>
                          <span className="flex-1 text-sm text-slate-800 truncate">{m.title}</span>
                          {m.completed ? (
                            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Concluído
                            </Badge>
                          ) : m.overdue ? (
                            <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Atraso
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-50 text-slate-600 border-slate-200">
                              <Clock className="h-3 w-3 mr-1" /> Pendente
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AnnualComplianceDashboard;
